import React from "react";
import { DatePicker, Select } from "antd";

const TextField = ({ label, value, placeholder = "Type here", onChange }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-slate-600">
      {label}
    </label>
    <input
      type="text"
      readOnly={!onChange}
      value={value || ""}
      placeholder={placeholder}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
    />
  </div>
);

const TextArea = ({
  label,
  value,
  rows = 3,
  placeholder = "Type here",
  onChange,
}) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-slate-600">
      {label}
    </label>
    <textarea
      rows={rows}
      readOnly={!onChange}
      value={value || ""}
      placeholder={placeholder}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
    />
  </div>
);

const EditPrescriptionConsultTab = ({
  findingsError,
  loadingFindings,
  selectedFindingIds,
  setSelectedFindingIds,
  findingsSelectOptions,
  primaryDiagnosisName,
  setPrimaryDiagnosisName,
  primaryIcdCode,
  setPrimaryIcdCode,
  secondaryDiagnosisName,
  setSecondaryDiagnosisName,
  secondaryIcdCode,
  setSecondaryIcdCode,
  diagnosisNameOptionsWithSelected,
  diagnosisCodeOptionsWithSelected,
  loadingDiagnosis,
  diagnosisNameToCode,
  diagnosisCodeToName,
  draftVisitDetails,
  updateDraftField,
  findings,
  severityValue,
  setSeverityValue,
  followUpVisitDate,
  setFollowUpVisitDate,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Findings</h3>
        {findingsError ? (
          <p className="mb-2 text-sm font-medium text-red-600">
            {findingsError}
          </p>
        ) : null}

        <Select
          mode="multiple"
          allowClear
          loading={loadingFindings}
          placeholder="Select findings"
          value={selectedFindingIds}
          onChange={(values) => setSelectedFindingIds(values)}
          options={findingsSelectOptions}
          className="w-full"
          style={{ width: "100%" }}
        />
      </div>

      <div className="rounded-md border border-slate-200 p-3">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">
          Provisional Diagnosis
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">
              Primary
            </label>
            <Select
              showSearch
              allowClear
              placeholder="Select diagnosis"
              value={primaryDiagnosisName || undefined}
              onChange={(val) => {
                const name = val || "";
                setPrimaryDiagnosisName(name);
                setPrimaryIcdCode(diagnosisNameToCode.get(name) || "");
              }}
              options={diagnosisNameOptionsWithSelected}
              loading={loadingDiagnosis}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">
              ICD-10 code
            </label>
            <Select
              showSearch
              allowClear
              placeholder="Select"
              value={primaryIcdCode || undefined}
              onChange={(val) => {
                const code = val || "";
                setPrimaryIcdCode(code);
                setPrimaryDiagnosisName(diagnosisCodeToName.get(code) || "");
              }}
              options={diagnosisCodeOptionsWithSelected}
              loading={loadingDiagnosis}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">
              Secondary
            </label>
            <Select
              showSearch
              allowClear
              placeholder="Select diagnosis"
              value={secondaryDiagnosisName || undefined}
              onChange={(val) => {
                const name = val || "";
                setSecondaryDiagnosisName(name);
                setSecondaryIcdCode(diagnosisNameToCode.get(name) || "");
              }}
              options={diagnosisNameOptionsWithSelected}
              loading={loadingDiagnosis}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">
              ICD-10 code
            </label>
            <Select
              showSearch
              allowClear
              placeholder="Select"
              value={secondaryIcdCode || undefined}
              onChange={(val) => {
                const code = val || "";
                setSecondaryIcdCode(code);
                setSecondaryDiagnosisName(diagnosisCodeToName.get(code) || "");
              }}
              options={diagnosisCodeOptionsWithSelected}
              loading={loadingDiagnosis}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>

      <TextArea
        label="Symptoms"
        value={draftVisitDetails?.symptoms || findings.join(", ")}
        rows={2}
        onChange={updateDraftField("symptoms")}
      />

      <div className="rounded-md border border-slate-200 p-3">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">
          History of Presenting Illness
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextField
            label="Comments"
            value={draftVisitDetails?.comments}
            onChange={updateDraftField("comments")}
          />
          <TextField
            label="Frame"
            value={draftVisitDetails?.frame}
            onChange={updateDraftField("frame")}
          />
          <TextField
            label="Coating"
            value={draftVisitDetails?.coating}
            onChange={updateDraftField("coating")}
          />
          <TextField
            label="NV Assessment"
            value={draftVisitDetails?.nvAssessment}
            onChange={updateDraftField("nvAssessment")}
          />
          <TextField
            label="DV Assessment"
            value={draftVisitDetails?.dvAssessment}
            onChange={updateDraftField("dvAssessment")}
          />
          <TextField
            label="Treatment Option"
            value={draftVisitDetails?.treatmentOption}
            onChange={updateDraftField("treatmentOption")}
          />
          <TextField
            label="Any Surgery Recommendation?"
            value={draftVisitDetails?.surgeryRecommendation}
            placeholder="Select"
            onChange={updateDraftField("surgeryRecommendation")}
          />
          <TextArea
            label="Onset"
            value={draftVisitDetails?.onset}
            rows={2}
            onChange={updateDraftField("onset")}
          />
          <TextArea
            label="Character"
            value={draftVisitDetails?.characteristics}
            rows={2}
            onChange={updateDraftField("characteristics")}
          />
          <TextArea
            label="Radiation"
            value={draftVisitDetails?.radiation}
            rows={2}
            onChange={updateDraftField("radiation")}
          />
          <TextArea
            label="Associated Symptoms"
            value={draftVisitDetails?.associatedSymptoms}
            rows={2}
            onChange={updateDraftField("associatedSymptoms")}
          />
          <TextArea
            label="Timing"
            value={draftVisitDetails?.timing}
            rows={2}
            onChange={updateDraftField("timing")}
          />
          <TextArea
            label="Exacerbating Factors"
            value={draftVisitDetails?.exacerbatingFactors}
            rows={2}
            onChange={updateDraftField("exacerbatingFactors")}
          />
          <TextArea
            label="Relieving Factors"
            value={draftVisitDetails?.relievingFactors}
            rows={2}
            onChange={updateDraftField("relievingFactors")}
          />

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Severity
            </label>
            <Select
              allowClear
              placeholder="Select Severity"
              value={severityValue}
              onChange={(val) => {
                setSeverityValue(val);
                updateDraftField("severity")(val);
              }}
              options={Array.from({ length: 10 }, (_, i) => {
                const v = String(i + 1);
                return { value: v, label: v };
              })}
              style={{ width: "100%" }}
            />
          </div>

          <TextArea
            label="Any Treatment Taken"
            value={draftVisitDetails?.treatmentTaken}
            rows={2}
            onChange={updateDraftField("treatmentTaken")}
          />
        </div>
      </div>

      <div className="rounded-md border border-slate-200 p-3">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">
          Other Details
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextField
            label="Consult Reason"
            value={draftVisitDetails?.consultReason}
            onChange={updateDraftField("consultReason")}
          />
          <TextField
            label="Recommendation"
            value={draftVisitDetails?.recommendation}
            onChange={updateDraftField("recommendation")}
          />
          <div className="md:col-span-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Schedule Follow up
            </label>
            <DatePicker
              value={followUpVisitDate}
              onChange={(d) => {
                setFollowUpVisitDate(d);
                updateDraftField("followUpVisitDate")(
                  d ? d.format("YYYY-MM-DD") : ""
                );
              }}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPrescriptionConsultTab;

